<template>
    <div>
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>Title:</b-th>
                    <b-th>Summary:</b-th>
                    <b-th>Reference:</b-th>
                    <b-th>Added By:</b-th>
                    <b-th>Added:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(doc, i) in documents" :key="i">
                    <b-td>
                        <input type="hidden" name="id" :value="doc.id" />
                        <a
                            v-if="doc.module === 'attachment'"
                            :href="doc.filePath"
                            target="_blank"
                        >
                            {{ doc.title ? doc.title : doc.filename }}
                        </a>
                        <a v-else href="#" @click.prevent="editLetter(doc.id)">
                            {{ doc.title }}
                        </a>
                    </b-td>

                    <b-td>
                        <div v-if="doc.module === 'attachment'">
                            {{ doc.summary }}
                        </div>
                    </b-td>

                    <b-td>
                        <span v-if="doc.source_type == 6">
                            LifeLearn ClientEd
                        </span>
                        <span v-else>
                            {{ doc.source ? doc.source.full_name : "-" }}
                        </span>
                    </b-td>

                    <b-td>
                        {{ doc.create_by.full_name }}
                    </b-td>
                    <b-td>
                        {{ properDate(doc.create_at) }}
                    </b-td>

                    <b-td
                        class="options"
                        v-if="isLoadingPrintRecordId !== doc.id"
                    >
                        <b-dropdown
                            v-if="
                                doc.module === 'attachment' &&
                                doc.isEditable &&
                                doc.isEditable.status
                            "
                            size="sm"
                            variant="link"
                            right
                            no-caret
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>
                            <b-dropdown-item
                                v-if="
                                    _hasAccess('client.attachment.update') &&
                                    isDocImage(doc.filename)
                                "
                                @click="annotate(doc)"
                            >
                                <i class="cus-picture-edit"></i>
                                Annotate Image
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-if="_hasAccess('client.attachment.update')"
                                @click="editAttachment(doc)"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Attachment
                            </b-dropdown-item>
                            <b-dropdown-item
                                @click="emailAttachment(doc)"
                                v-if="_hasAccess('client.attachment.update')"
                            >
                                <i class="cus-email"></i> Email Attachment
                            </b-dropdown-item>
                            <b-dropdown-item
                                v-if="_hasAccess('client.attachment.delete')"
                                @click="promptDelete('attachment', doc.id)"
                            >
                                <i class="cus-delete"></i> Delete Attachment
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>

                    <b-td class="options" v-else>
                        <moon-loader color="black" size="16px" />
                    </b-td>
                </b-tr>
                <b-tr v-if="documents.length === 0">
                    <b-td colspan="6">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="showPagination"
            class="vet-pagination"
            align="right"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="changePage"
        />

        <modal-email-attachment ref="modalEmailAttachment" />

        <modal-delete-document
            ref="modalDeleteDocument"
            @onSuccess="afterDeleted"
        />

        <modal-add-attachment
            ref="modalAddAttachment"
            @onSuccess="afterDeleted"
        />

        <Annotate
            v-if="isShowImageEditor"
            @close="closeImageEditor"
            module="client"
            :file="imageEditor.file"
            :filename="imageEditor.filename"
            :name="imageEditor.name"
            :imageUrl="imageEditor.image"
            :entityId="imageEditor.entityId"
        />
    </div>
</template>

<script>
import ClientDocument from "@/services/client-documents";
import Loader from "@/components/elements/loader-list";
import ModalEmailAttachment from "@/components/documents/document-email-attachment-modal.vue";
import ModalDeleteDocument from "@/components/client-documents/document-delete-attachment-modal";
import ModalAddAttachment from "@/components/client-documents/document-add-attachment-modal";
import MoonLoader from "vue-spinner/src/MoonLoader.vue";
import Annotate from "@/components/commons/annotate";

export default {
    components: {
        Loader,
        ModalEmailAttachment,
        ModalDeleteDocument,
        ModalAddAttachment,
        MoonLoader,
        Annotate
    },
    props: {
        clientName: {
            type: String
        }
    },
    data() {
        return {
            documents: [],
            isLoading: false,
            clientId: this.$store.state.client.client_id,
            isLoadingPrintRecordId: false,
            currentPage: 1,
            totalRows: 0,
            perPage: 0,
            showPagination: false,
            isShowImageEditor: false,
            imageEditor: {
                file: "",
                name: "",
                image: "",
                entityId: null
            }
        };
    },
    computed: {
        userId() {
            return this.$route.params.id;
        }
    },
    mounted() {
        this.getDocumentsDelay();
    },
    methods: {
        closeImageEditor() {
            this.isShowImageEditor = false;
        },
        isDocImage(filename = "") {
            let ext = filename.split(".").pop().toLowerCase();
            return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
        },
        annotate(doc) {
            this.imageEditor = {
                file: doc.title,
                name: this.clientName,
                image: doc.filePath,
                entityId: doc.client_id,
                filename: doc.filename
            };

            this.isShowImageEditor = true;
        },
        editAttachment(attachment) {
            let params = {
                action: "edit",
                attachment: attachment,
                module: this.MODULE.CLIENT,
                client_id: this.$store.state.client.client_id
            };
            this.$refs.modalAddAttachment.open(params);
        },
        async getDocuments(page) {
            this.isLoading = true;

            const { resources, meta } = await ClientDocument.getDocuments({
                type: "attachment",
                page: page,
                clientId: this.$store.state.client.client_id
            });

            this.documents = resources;
            this.isLoading = false;

            // TODO : enhancement : Below meta can be deleted as already used the endpoint of webform count but need also to remove from backend and handle the endpoint each time add/delete attachment
            // update tab menu items counts
            if (meta) {
                // pagination
                this.currentPage = meta.currentPage;
                this.totalRows = meta.total;
                this.perPage = meta.perPage;
                this.showPagination = meta.lastPage > 1; // show pagination if there's more than 1 page

                // counts
                if (meta.count) {
                    this.$emit("updateCounts", {
                        all: meta.count.all_count.toString(),
                        attachment: meta.count.attachment_count.toString(),
                        letter: meta.count.letter_count.toString(),
                        webform: meta.count.webform_count.toString()
                    });
                }
            }
        },
        properDate(rawDate) {
            if (rawDate) {
                return this._toDateTime(rawDate).toFormat(
                    this._getLuxonFormat()
                );
            }
            return "-";
        },
        promptDelete(type, documentId) {
            this.$refs.modalDeleteDocument.open({
                type: type,
                id: documentId
            });
        },
        getDocumentsDelay() {
            let self = this;
            this.isLoading = true;
            setTimeout(() => {
                self.getDocuments(1);
            }, 2000);
        },
        afterDeleted() {
            this.getDocuments();
        },
        emailLetter(document) {
            this.$refs.modalEmailLetter.open(document);
        },
        async printLetter(documentId) {
            this.isLoadingPrintRecordId = documentId;
            const pdfPath = await ClientDocument.getPDFLetterURI(documentId);
            this.isLoadingPrintRecordId = null;

            if (pdfPath) {
                // eslint-disable-next-line no-undef
                window.open(path, "_blank");
            } else {
                this._message("Error: Unable to print", "danger");
            }
        },
        changePage(nextPage) {
            this.getDocuments(nextPage);
        },
        emailAttachment(record) {
            record.recipient = this.$store.state.client
                ? this.$store.state.client.client_details
                : null;

            this.$refs.modalEmailAttachment.open(record);
        }
    }
};
</script>

<style scoped>
td.options {
    width: 50px;
    text-align: right;
}
.vet-pagination {
    margin-top: 50px;
}
</style>
